import _isEqual from 'lodash/isEqual'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import Link from 'next/link'
import { memo, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import { ICON_SIZE, MAX_LIST_WIDTH } from '@nxt-insurance/ui-kit/constants'
import { borderRadius } from '@nxt-insurance/ui-kit/styles/borderRadius'
import { bgColor, borderColor } from '@nxt-insurance/ui-kit/styles/colors'
import { gapX } from '@nxt-insurance/ui-kit/styles/gap'
import { paddingX, paddingY } from '@nxt-insurance/ui-kit/styles/padding'
import type { IconSize } from '@nxt-insurance/ui-kit/types'

import { runQuery, sanityClientLike } from '@/cms/client'
import { borderRadiusEnum, colorEnum, spaceEnum } from '@/cms/helperTypes'
import { imageAssetQuery } from '@/cms/image'
import { webUrlSchema } from '@/cms/webUrl'
import { Icon } from '@/components/Icon'
import { BlockContent } from '@/components/renderers/BlockContent'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { useWebUrlRel } from '@/hooks/useWebUrlRel'
import { getContrastColor } from '@/utils/style'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import { useQuery } from '@tanstack/react-query'

import { ConfigurableTypography, configurableTypography } from './ConfigurableTypography'

const listItemSchema = z.object({
  _key: z.string(),
  _type: z.literal('listItem'),
  title: configurableTypography,
  subtitle: configurableTypography.optional(),
  content: z.array(z.unknown()).optional(),

  borderColor: colorEnum.optional(),
  backgroundColor: colorEnum.optional(),
  paddingX: spaceEnum.optional(),
  paddingY: spaceEnum.optional(),
  space: spaceEnum.optional(),
  borderRadius: borderRadiusEnum.optional(),
  borderWidth: z.number().optional().default(1),
  image: z
    .object({
      _type: z.literal('image'),
      width: z.enum(Object.keys(ICON_SIZE) as [IconSize, ...IconSize[]]).optional(),
      customWidth: z.number().optional(),
      alt: z.string(),
      asset: z
        .object({
          _type: z.literal('reference'),
          _ref: z.string(),
        })
        .optional(),
    })
    .optional(),
  href: webUrlSchema.optional(),
  maxWidth: z.number().optional(),
  filter: z
    .object({
      key: z.string(),
      value: z.string(),
    })
    .optional(),
})

const ListItemContent = (props: { item: z.infer<typeof listItemSchema> }) => {
  const {
    item: {
      image,
      content,
      maxWidth,
      title,
      subtitle,
      backgroundColor,
      href,
      paddingX: paddingXName,
      paddingY: paddingYName,
    },
  } = props
  const { data: derefedImage } = useQuery({
    queryKey: ['image', image?.asset?._ref],
    queryFn: async () => runQuery(imageAssetQuery, { id: image?.asset?._ref }),
    enabled: image?.asset !== undefined,
  })
  //if customWidth property is set in Sanity, the image is treated as a regular image - otherwise as an icon
  const imageWidth = (image?.width && ICON_SIZE[image.width]) || image?.customWidth || 0
  const imageProps = useNextSanityImage(sanityClientLike, image ?? null, {
    imageBuilder: (imageUrlBuilder) =>
      image ? imageUrlBuilder.width(imageWidth * 2) : imageUrlBuilder,
  })

  return (
    <>
      {derefedImage && image && imageProps && paddingXName && paddingYName ? (
        <div
          className={twMerge(
            `flex flex-row items-center overflow-hidden`,
            Boolean(image.width) && !image.customWidth && 'h-full',
            paddingX({ space: paddingXName ?? 'gutter' }),
            paddingY({ space: paddingYName ?? 'small' }),
            paddingYName !== 'none' && 'my-auto'
          )}
        >
          <Image
            {...imageProps}
            width={imageWidth}
            blurDataURL={derefedImage.metadata.lqip}
            placeholder="blur"
            alt={image.alt}
            className={twMerge(
              'object-cover',
              image.customWidth && paddingYName === 'none' && 'h-full'
            )}
          />
        </div>
      ) : null}
      <div className={twMerge('flex flex-1 flex-col justify-center p-5')}>
        <ConfigurableTypography
          node={{
            index: 0,
            isInline: false,
            renderNode: () => null,
            value: { _key: '1', ...title },
          }}
          align="left"
        />
        {subtitle ? (
          <ConfigurableTypography
            node={{
              index: 0,
              isInline: false,
              renderNode: () => null,
              value: { _key: '1', ...subtitle },
            }}
            align="left"
          />
        ) : null}
        <BlockContent blocks={content || []} width={maxWidth ?? 450} sectionName={' '} />{' '}
      </div>
      {href ? (
        <Icon
          size="small"
          icon={{
            name: getContrastColor(backgroundColor ?? 'uiLight').includes('Light')
              ? 'action.chevronRightUiLight_24'
              : 'action.chevronRight_24',
          }}
        />
      ) : null}
    </>
  )
}

export const ListItem = memo(
  (props: { node: PortableTextTypeComponentProps<unknown>; sectionName: string }) => {
    const { track } = useAnalytics()
    const value = useMemo(() => listItemSchema.parse(props.node.value), [props.node.value])
    const { href } = value
    const parsedHref = useUrlFromWebUrlReference(href, {
      optional: true,
    })
    const rel = useWebUrlRel(href)

    const filterValue = useWatch({ name: value.filter?.key ?? '' })

    if (value.filter?.value && value.filter.value !== filterValue) {
      return null
    }

    if (!value.title) {
      return null
    }
    const sharedContainerProps = {
      $backgroundColor: value.backgroundColor ?? undefined,
      $borderColor: value.borderColor ?? undefined,
      $borderRadius: value.borderRadius ?? undefined,
      $space: value.space ?? undefined,
      $borderWidth: value.borderWidth,
      $maxWidth: value.maxWidth ?? undefined,
    }

    if (!href) {
      return (
        <div
          className={twMerge(
            `flex h-full w-full flex-row overflow-hidden border-solid`,
            bgColor({
              color: sharedContainerProps.$backgroundColor ?? 'uiLight',
            }),
            borderColor({
              color: sharedContainerProps.$borderColor ?? 'uiLight',
            }),
            gapX({
              space: sharedContainerProps.$space ?? 'gutter',
            }),
            borderRadius({
              radius: sharedContainerProps.$borderRadius ?? 'small',
            })
          )}
          style={{
            maxWidth: `${sharedContainerProps.$maxWidth ?? MAX_LIST_WIDTH}px`,
          }}
        >
          <ListItemContent item={value} />
        </div>
      )
    }

    return (
      <Link
        className={twMerge(
          'flex w-full flex-row items-center overflow-hidden transition-opacity delay-300 ease-in-out',
          sharedContainerProps.$backgroundColor === 'uiLight' && 'hover:bg-ui-grey-50',
          'hover:opacity-70',
          bgColor({
            color: sharedContainerProps.$backgroundColor ?? 'uiLight',
          }),
          borderColor({
            color: sharedContainerProps.$borderColor ?? 'uiLight',
          }),
          gapX({
            space: sharedContainerProps.$space ?? 'gutter',
          }),
          borderRadius({
            radius: sharedContainerProps.$borderRadius ?? 'small',
          })
        )}
        style={{
          maxWidth: `${sharedContainerProps.$maxWidth ?? MAX_LIST_WIDTH}px`,
          border: `${sharedContainerProps.$borderWidth}px solid`,
        }}
        href={parsedHref}
        rel={rel}
        target={rel ? '_blank' : undefined}
        onClick={() => {
          track(EVENTS.ProductListClicked, {
            title: value.title,
            sectionName: props.sectionName,
          })
        }}
        {...sharedContainerProps}
      >
        <ListItemContent item={value} />
      </Link>
    )
  },
  _isEqual
)
